import { persistor, store } from "../store/store";
import { actions as CommonActions } from "../store/commonRedux/actions";

const AUTH_PERSIST_KEY = "persist:taskamoon-auth";

/** How long a queued «وارد شدی» notice stays worth showing. */
const SWITCH_NOTICE_TTL_MS = 30 * 1000;

/** Time the splash gets to paint before the reload takes the page away. */
const SPLASH_LEAD_MS = 260;

/**
 * The payload behind `components/OrgSwitchSplash`.
 *
 * One object serves both halves of the switch: React raises the splash from it
 * immediately, over the old panel, and — because it is written to the persisted
 * `common` slice and flushed before the reload — raises the same screen again
 * from the same object as soon as it mounts in the new document. `at` is
 * stamped once, here, so the minimum on-screen time is measured from the click
 * rather than restarted by the reload.
 */
const switchSplashPayload = (org) => ({
  title: org?.title || "",
  avatar: org?.avatar?.original_url || null,
  at: Date.now(),
});

const persistOrganizationSync = (org) => {
  try {
    const raw = window.localStorage.getItem(AUTH_PERSIST_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);

    parsed.organization = JSON.stringify(org ?? null);
    window.localStorage.setItem(AUTH_PERSIST_KEY, JSON.stringify(parsed));
  } catch (e) {}
};

/**
 * Every way of changing the active organization ends here — the workspace
 * switcher, the org page, finishing the setup wizard, registering a second
 * workspace, and a notification that belongs to another one. So this is also
 * the one place that has to say what just happened: the switch reloads the
 * whole app, and without a word afterwards the user is looking at somebody
 * else's projects with no idea why.
 *
 * The notice is parked in the persisted `common` slice and flushed to storage
 * before the reload, because nothing in memory survives it. The layout picks it
 * up on the way back and clears it.
 */
export const switchOrganization = (
  org,
  _queryClient,
  _dispatch,
  redirectTo = "/",
) => {
  if (org) persistOrganizationSync(org);

  /* One frame of breathing room before the document is torn down. Without it
     the browser can go straight from the click to the unload without ever
     painting the overlay, and the switch looks like the old panel freezing —
     exactly what the overlay is there to prevent. */
  const done = () =>
    window.setTimeout(() => window.location.replace(redirectTo), SPLASH_LEAD_MS);

  if (!org?.title) {
    done();
    return;
  }

  try {
    store.dispatch(
      CommonActions.setVariables({
        orgSwitchPending: switchSplashPayload(org),
        orgSwitchNotice: { title: org.title, at: Date.now() },
      }),
    );
    /* `flush()` is what makes this safe: redux-persist writes on a throttle, and
       `location.replace` would otherwise beat it to the tape — the splash and
       the arrival notice both have to be on disk before the document goes. A
       failure here is never a reason to leave the user on a page that no longer
       matches the organization they picked, so the redirect happens either
       way. */
    Promise.resolve(persistor.flush()).catch(() => {}).then(done);
  } catch (e) {
    done();
  }
};

/**
 * The queued notice, if there is one worth showing. Anything older than
 * `SWITCH_NOTICE_TTL_MS` is a leftover — a reload that never finished, a tab
 * closed mid-switch — and saying «وارد سازمان … شدی» about it days later would
 * be noise at best and confusing at worst.
 */
export const takeOrgSwitchNotice = (notice) => {
  if (!notice?.title) return null;
  if (notice.at && Date.now() - notice.at > SWITCH_NOTICE_TTL_MS) return null;

  return notice;
};

export const toInternalNotificationPath = (action) => {
  try {
    const url = new URL(action, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return action;
  }
};

/**
 * Notifications are user-scoped, but project/task endpoints are scoped by the
 * active organization header. Persist the task's organization before loading
 * its action so the boot request hydrates the matching roles and permissions.
 */
export const openNotificationAction = (notification, navigate) => {
  const action = notification?.data?.action;
  if (!action) return;

  const path = toInternalNotificationPath(action);
  /* Task notifications carry the organization on the task itself; the ones that
     are not about a task — a support ticket, say — put it at the top level. */
  const targetOrganizationId =
    notification?.data?.task?.organization_id ??
    notification?.data?.organization_id;
  const { organization, allOrganizations = [] } = store.getState().auth;

  if (
    targetOrganizationId != null &&
    String(organization?.id) !== String(targetOrganizationId)
  ) {
    const target = allOrganizations.find(
      (org) => String(org?.id) === String(targetOrganizationId),
    );
    if (target) {
      switchOrganization(target, null, null, path);
      return;
    }
  }

  if (navigate) navigate(path);
  else window.location.assign(path);
};
